/**
 * User Team Membership API Service
 * Handles multi-team membership operations
 */

import api from './api';

export interface UserTeamMembership {
  id: string;
  teamId: string;
  teamName: string;
  teamDescription: string | null;
  role: string | null;
  createdAt: string;
}

export interface AddTeamMembershipRequest {
  teamId: string;
  role?: string | null;
}

export interface UpdateTeamMembershipRequest {
  role?: string | null;
}

/**
 * Get user's team memberships
 */
export const getUserTeams = async (
  userId: string
): Promise<UserTeamMembership[]> => {
  const response = await api.get<{
    success: boolean;
    data: { teams: UserTeamMembership[] };
  }>(`/users/${userId}/teams`);

  return response.data.data.teams;
};

/**
 * Add team membership for a user
 */
export const addUserTeam = async (
  userId: string,
  data: AddTeamMembershipRequest
): Promise<UserTeamMembership> => {
  const response = await api.post<{
    success: boolean;
    data: { membership: UserTeamMembership };
    message: string;
  }>(`/users/${userId}/teams`, data);

  return response.data.data.membership;
};

/**
 * Update team membership (role)
 */
export const updateUserTeam = async (
  userId: string,
  teamId: string,
  data: UpdateTeamMembershipRequest
): Promise<void> => {
  await api.patch(`/users/${userId}/teams/${teamId}`, data);
};

export interface AffectedTeamOKRsResult {
  count: number;
  okrs: Array<{ id: string; title: string }>;
}

/**
 * Check for OKRs affected by team removal
 */
export const checkAffectedTeamOKRs = async (
  userId: string,
  teamId: string
): Promise<AffectedTeamOKRsResult> => {
  const response = await api.get<{
    success: boolean;
    data: AffectedTeamOKRsResult;
  }>(`/users/${userId}/teams/${teamId}/affected-okrs`);

  return response.data.data;
};

/**
 * Remove team membership
 */
export const removeUserTeam = async (
  userId: string,
  teamId: string,
  force: boolean = false
): Promise<{ clearedOKRs: number }> => {
  const response = await api.delete<{
    success: boolean;
    message: string;
    data: { clearedOKRs: number };
  }>(`/users/${userId}/teams/${teamId}${force ? '?force=true' : ''}`);

  return response.data.data;
};

/**
 * Update team role
 */
export const updateTeamRole = async (
  userId: string,
  teamId: string,
  role: string | null
): Promise<void> => {
  await updateUserTeam(userId, teamId, { role });
};
